import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Launch as LaunchIcon,
  Code as CodeIcon,
} from '@mui/icons-material';

const Projects = () => {
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All' },
    { value: 'web', label: 'Web Apps' },
    { value: 'mobile', label: 'Mobile Apps' },
  ];

  const projects = [
    {
      id: 1,
      title: 'CodePeek',
      description:
        'A QR and barcode reader that tells you what a code actually contains before you act on it. Point your camera at a code, or drop in a screenshot, and it pulls out the link, Wi-Fi details, contact card or product number and lays them out plainly. For links it shows the real domain up front, so a lookalike or shortened URL has nowhere to hide. Everything runs in the browser — no image or scanned text ever leaves the device. Installs as an app on Android and iPhone and keeps working offline.',
      image: 'https://codepeek.teniquasystems.com/icons/og.png',
      category: 'web',
      technologies: ['TypeScript', 'Vite', 'PWA', 'ZXing (WASM)'],
      liveUrl: 'https://codepeek.teniquasystems.com/',
      status: 'live',
      featured: true,
    },
    {
      id: 3,
      title: 'The Body Calculator',
      description:
        'A set of more than thirty research-backed health calculators that run entirely in your browser. BMI, body fat and ideal weight; cholesterol ratio, Framingham and CHADS-VASc heart-risk scores; eGFR and other kidney measures; BMR, TDEE, macros and calorie targets; fitness numbers like VO2 max, one-rep max and heart-rate zones; plus screeners such as GAD-7 and STOP-BANG. Each one shows your result against the target range for context, in plain language. Nothing you enter is ever sent anywhere — every calculation happens on your device.',
      image: 'https://thebodycalc.com/assets/img/og-image.svg',
      category: 'web',
      technologies: ['HTML', 'JavaScript', 'Cloudflare Workers', 'Static PWA'],
      liveUrl: 'https://thebodycalc.com/',
      status: 'live',
      featured: true,
    },
    {
      id: 4,
      title: 'Dice',
      description:
        'A dice roller you cannot lose down the back of the sofa. Roll a full set of polyhedral dice — d4, d6, d8, d10, d12 and d20 — with realistic sound and haptics that make it feel like the real thing. There is a dedicated Catan Cities & Knights mode for the event die, and a Magic 8-Ball for the big decisions. No ads, no accounts, and it works offline once you install it. Born out of the very specific frustration of setting up a board game and discovering the dice have gone missing.',
      image: 'https://dice.teniquasystems.com/og-image.png',
      category: 'web',
      technologies: ['HTML', 'JavaScript', 'PWA'],
      liveUrl: 'https://dice.teniquasystems.com/',
      status: 'live',
      featured: true,
    },
    {
      id: 2,
      title: 'Leru',
      description:
        'A weather app we are building for people who just want the answer: do I need a jacket, and is the rain going to hold off. Clean forecast, hourly and daily views, and the current conditions without a screen full of clutter. Currently in development — the first release is on the way.',
      category: 'mobile',
      technologies: ['Flutter', 'Dart'],
      status: 'in-development',
      featured: false,
    },
  ];

  const filteredProjects =
    selectedCategory === 'all'
      ? projects
      : projects.filter((project) => project.category === selectedCategory);

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Our Projects
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9 }}>
            The apps we have shipped, and the ones we are working on next
          </Typography>
        </Container>
      </Box>

      {/* Category Tabs */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minWidth: 120,
                textTransform: 'none',
                fontSize: '1rem',
              },
            }}
          >
            {categories.map((category) => (
              <Tab
                key={category.value}
                value={category.value}
                label={category.label}
              />
            ))}
          </Tabs>
        </Box>

        {/* Projects Grid */}
        <Grid container spacing={4}>
          {filteredProjects.map((project) => (
            <Grid item xs={12} md={6} key={project.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    transition: 'transform 0.3s ease-in-out',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <Chip
                  label={project.status === 'live' ? 'Live' : 'In development'}
                  color={project.status === 'live' ? 'primary' : 'default'}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 1,
                  }}
                />

                {project.image ? (
                  <CardMedia
                    component="img"
                    height="220"
                    image={project.image}
                    alt={`${project.title} screenshot`}
                    sx={{ objectFit: 'cover', backgroundColor: theme.palette.grey[100] }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 220,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      color: 'white',
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 700, opacity: 0.9 }}>
                      {project.title}
                    </Typography>
                  </Box>
                )}

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {project.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {project.description}
                  </Typography>

                  <Box sx={{ mb: 1 }}>
                    {project.technologies.map((tech, index) => (
                      <Chip
                        key={index}
                        label={tech}
                        size="small"
                        variant="outlined"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </CardContent>

                {project.liveUrl && (
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<LaunchIcon />}
                      onClick={() => window.open(project.liveUrl, '_blank', 'noopener')}
                    >
                      Open {project.title}
                    </Button>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredProjects.length === 0 && (
          <Box textAlign="center" sx={{ py: 8 }}>
            <CodeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nothing in this category yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              More projects are on the way — check back soon.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Projects;
